import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { Users as UsersIcon, Search, Shield, ShieldOff, Trash2, Phone, Mail, RefreshCw, UserCheck, UserX, Crown, MessageSquare } from 'lucide-react';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.users();
      setUsers(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (id) => {
    try {
      await adminAPI.toggleUser(id);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const setRole = async (id, role) => {
    const u = users.find(x => x.id === id);
    const names = { customer: 'Courier', staff: 'Staff', admin: 'Admin' };
    if (!window.confirm(`Change ${u ? u.email : 'this user'} to ${names[role] || role}?` +
        (role === 'admin' ? '\n\nAdmins can change prices, roles and remove users.' : ''))) { load(); return; }
    try {
      await adminAPI.setUserRole(id, role);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Remove user ${email}? They can no longer log in. Their parcels and payments stay in the history.`)) return;
    try {
      await adminAPI.deleteUser(id);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const resendSms = async (id, phone) => {
    try {
      await adminAPI.resendSms(id);
      alert(`SMS verification code sent to ${phone}`);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').includes(q) ||
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.id_number || '').includes(q);
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && u.is_active) ||
      (filterStatus === 'inactive' && !u.is_active) ||
      (filterStatus === 'verified' && u.phone_verified) ||
      (filterStatus === 'unverified' && !u.phone_verified);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const stats = {
    total: users.length,
    couriers: users.filter(u => u.role === 'customer').length,
    admins: users.filter(u => u.role === 'admin').length,
    active: users.filter(u => u.is_active).length,
    verified: users.filter(u => u.phone_verified).length,
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Users / Couriers</h1>
        <button style={s.outlineBtn} onClick={load}><RefreshCw size={14}/> Refresh</button>
      </div>

      {/* Stats bar */}
      <div style={s.statsBar}>
        <div style={s.statItem}><UsersIcon size={14}/> <b>{stats.total}</b> Total</div>
        <div style={s.statItem}>🚚 <b>{stats.couriers}</b> Couriers</div>
        <div style={s.statItem}><Crown size={14}/> <b>{stats.admins}</b> Admins</div>
        <div style={s.statItem}><UserCheck size={14} color="#8BC63E"/> <b>{stats.active}</b> Active</div>
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <div style={s.searchWrap}>
          <Search size={14} style={s.searchIcon}/>
          <input style={s.searchInput}
            placeholder="Search by name, email, phone, or ID..."
            value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <select style={s.select} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="customer">Couriers</option>
          <option value="admin">Admins</option>
        </select>
        <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Disabled</option>

        </select>
      </div>

      {/* Users table */}
      <div style={s.panel}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Phone</th>
              <th style={s.th}>ID Number</th>
              <th style={s.th}>Role</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Registered</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{...s.td, textAlign:'center', padding:40, color:'#8a8da4'}}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} style={{...s.td, textAlign:'center', padding:40, color:'#8a8da4'}}>No users found</td></tr>
            ) : filtered.map(u => {
              const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.full_name || '—';
              return (
                <tr key={u.id}>
                  <td style={s.td}>
                    <div style={{fontWeight:600, color:'#e4e5ed'}}>{name}</div>
                  </td>
                  <td style={s.td}>
                    <div style={{display:'flex', alignItems:'center', gap:6, fontSize:12}}>
                      <Mail size={12} color="#8789a0"/> {u.email}
                    </div>
                  </td>
                  <td style={s.td}>
                    {u.phone ? (
                      <span style={{fontFamily:"'Space Mono', monospace", fontSize:12}}>{u.phone}</span>
                    ) : <span style={{color:'#8a8da4'}}>—</span>}
                  </td>
                  <td style={s.td}>
                    <span style={{fontFamily:"'Space Mono', monospace", fontSize:12}}>{u.id_number || '—'}</span>
                  </td>
                  <td style={s.td}>
                    <select style={{...s.roleSelect, color: u.role === 'admin' ? '#ffb020' : '#8789a0'}}
                      value={u.role} onChange={e => setRole(u.id, e.target.value)}>
                      <option value="customer">Courier</option>
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td style={s.td}>
                    {u.is_active ? (
                      <span style={s.badgeGreen}><UserCheck size={10}/> Active</span>
                    ) : (
                      <span style={s.badgeRed}><UserX size={10}/> Disabled</span>
                    )}
                  </td>
                  <td style={s.td}>
                    <span style={{fontSize:11, color:'#8789a0'}}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('ka-GE') : '—'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{display:'flex', gap:6}}>
                      <button style={s.smBtn} title={u.is_active ? 'Disable user' : 'Enable user'}
                        onClick={() => toggleActive(u.id)}>
                        {u.is_active ? <ShieldOff size={12}/> : <Shield size={12}/>}
                      </button>
                      <button style={{...s.smBtn, background:'rgba(255,68,102,0.1)', color:'#ff4466'}}
                        title="Delete user" onClick={() => deleteUser(u.id, u.email)}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const s = {
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 },
  title: { fontSize:22, fontWeight:700, color:'#e4e5ed' },
  outlineBtn: { display:'inline-flex', alignItems:'center', gap:6, background:'transparent', border:'1px solid #252733', color:'#e4e5ed', padding:'8px 12px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' },
  statsBar: { display:'flex', gap:20, background:'#0f1117', border:'1px solid #252733', borderRadius:10, padding:'14px 20px', marginBottom:14 },
  statItem: { display:'flex', alignItems:'center', gap:6, fontSize:13, color:'#8789a0' },
  filters: { display:'flex', gap:10, marginBottom:16 },
  searchWrap: { flex:1, position:'relative' },
  searchIcon: { position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#8a8da4' },
  searchInput: { width:'100%', background:'#0f1117', border:'1px solid #252733', borderRadius:8, padding:'10px 12px 10px 36px', fontSize:13, color:'#e4e5ed', outline:'none', boxSizing:'border-box' },
  select: { background:'#0f1117', border:'1px solid #252733', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#e4e5ed', cursor:'pointer', outline:'none' },
  panel: { background:'#0f1117', border:'1px solid #252733', borderRadius:12, overflow:'hidden' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 16px', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:0.8, color:'#8a8da4', background:'#0a0b0e', borderBottom:'1px solid #252733' },
  td: { padding:'12px 16px', fontSize:13, borderBottom:'1px solid #1a1b25', color:'#e4e5ed' },
  badgeGreen: { display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:6, fontSize:10, fontWeight:600, background:'rgba(139,198,62,0.12)', color:'#8BC63E' },
  badgeRed: { display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:6, fontSize:10, fontWeight:600, background:'rgba(255,68,102,0.12)', color:'#ff4466' },
  verifiedBadge: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:16, height:16, borderRadius:'50%', background:'#8BC63E', color:'#07080c', fontSize:10, fontWeight:900 },
  unverifiedBadge: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:16, height:16, borderRadius:'50%', background:'#ffb020', color:'#07080c', fontSize:10, fontWeight:900 },
  resendBtn: { display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:6, fontSize:10, fontWeight:600, background:'rgba(255,176,32,0.15)', color:'#ffb020', border:'1px solid rgba(255,176,32,0.3)', cursor:'pointer' },
  smBtn: { display:'inline-flex', alignItems:'center', gap:4, padding:'6px 10px', background:'#252733', border:'none', borderRadius:6, color:'#e4e5ed', fontSize:11, fontWeight:500, cursor:'pointer' },
  roleSelect: { background:'#161821', border:'1px solid #252733', borderRadius:6, padding:'4px 8px', fontSize:11, fontWeight:600, cursor:'pointer', outline:'none' },
};
